import { useMemo } from "react";
import { useTellescopeQueries } from "@tellescope/tanstack-query";
import { validateUrl } from "@tellescope/utils";

/**
 * Hook to fetch secure file URLs from an array of secure file names or validate existing URLs.
 *
 * This hook handles the common pattern in Tellescope where media content (avatars, images, attachments, etc.)
 * can be stored as either direct URLs or as secure file names that need to be fetched from the files API.
 * It processes multiple file identifiers in parallel using useTellescopeQueries.
 *
 * @param secureNames - Array of valid URLs or secure file names to fetch from the files API
 * @returns Array of file URLs in the same order as input, or undefined for unavailable files
 *
 * @example
 * ```tsx
 * // For multiple attachments
 * const imageUrls = useSecureFileUrls(attachments.map(a => a.secureName));
 * return imageUrls.map((url, i) => <img key={i} src={url} alt="Attachment" />);
 *
 * // For mixed URLs and secure names
 * const urls = useSecureFileUrls([user.avatar, attachment.secureName, 'https://example.com/image.jpg']);
 * ```
 */
export const useSecureFileUrls = (secureNames?: string[]): { url: string; secureName: string }[] => {
  const validationResults = useMemo(() => {
    if (!secureNames) return [];
    return secureNames.map((secureName) => {
      if (!secureName) return { isValidUrl: false, secureName };
      const result = validateUrl(secureName);
      return { isValidUrl: !result.error, secureName };
    });
  }, [secureNames]);

  // Get only the secure names that need to be fetched
  const secureNamesToFetch = useMemo(() => {
    return validationResults
      .filter((result) => result.secureName && !result.isValidUrl)
      .map((result) => result.secureName);
  }, [validationResults]);

  // Fetch download URLs for all secure names in parallel
  const queries = useTellescopeQueries({
    model: "files",
    method: "file_download_URL",
    requests: secureNamesToFetch.map((secureName) => ({
      secureName,
    })),
    staleTime: 4 * 60 * 1000, // 4 minutes (presigned URLs typically valid for 5 minutes)
    retry: 1, // Fast fail for missing files
  });

  // Map the results back to the original order
  return useMemo(() => {
    if (!secureNames) return [];

    // Create a map of secureName -> downloadURL for quick lookup
    const urlMap = new Map<string, string>();
    secureNamesToFetch.forEach((secureName, index) => {
      const downloadURL = queries[index]?.data?.downloadURL;
      if (downloadURL) {
        urlMap.set(secureName, downloadURL);
      }
    });

    // Return URLs in the same order as input
    return validationResults.reduce<{ url: string; secureName: string }[]>((acc, result) => {
      if (!result.secureName) return acc;
      if (result.isValidUrl) {
        acc.push({ url: result.secureName, secureName: result.secureName });
      } else {
        const url = urlMap.get(result.secureName);
        if (!url) return acc;
        acc.push({ url, secureName: result.secureName });
      }

      return acc;
    }, []);
  }, [secureNames, validationResults, queries, secureNamesToFetch]);
};
